/**
 * GraphQL type definitions matching backend schema — service pillar.
 * Split from graphql/types.ts (issue #1409).
 */

import type { PageInfo } from './common';
import type { Diagram } from './diagram';
import type { Team } from './team';
import type { ServiceTechnologyAssignment, Technology } from './technology';

/**
 * Service type enumeration
 */
export enum ServiceType {
  Application = 'Application',
  Integration = 'Integration',
  Platform = 'Platform',
  Infrastructure = 'Infrastructure',
  /** CMS-hosted or static website; identity is `url`, not `repo` (issue #1287). */
  Website = 'Website',
}

/**
 * Service lifecycle status enumeration
 */
export enum ServiceLifecycleStatus {
  Prototype = 'prototype',
  Development = 'development',
  Production = 'production',
  Deprecated = 'deprecated',
  Retired = 'retired',
  /** Not yet assessed (issue #1525, ADR-1319) — an honest pending state, never a fabricated default. */
  Unassessed = 'unassessed',
}

/**
 * Service tier enumeration (criticality)
 */
export enum ServiceTier {
  Tier0 = 'tier0',
  Tier1 = 'tier1',
  Tier2 = 'tier2',
  Tier3 = 'tier3',
  /** Not yet assigned (issue #1525, ADR-1319) — an honest pending state, never a fabricated default. */
  Unassigned = 'unassigned',
}

/**
 * Service entity
 */
/**
 * Assessment cadence for recurring quality assessments.
 * Controls how often the EventBridge scheduler creates assessments for a service.
 * Default (when absent) is 'semi-annual'.
 */
export enum AssessmentCadence {
  Quarterly = 'quarterly',
  SemiAnnual = 'semi-annual',
  Annual = 'annual',
}

export interface Service {
  entityId: string;
  entityType: string;
  name: string;
  shortName: string;
  serviceType: ServiceType;
  // Nullable: a service may be unowned (no team). Null owner renders as
  // "Unassigned" and can be claimed/assigned (issue #536).
  teamId: string | null;
  team?: Team | null;
  lifecycleStatus: ServiceLifecycleStatus;
  tier: ServiceTier;
  repo?: string | null;
  /** Hosted-site URL — the identity of a `Website` service, which has no repository (issue #1287). */
  url?: string | null;
  description?: string | null;
  tags?: string[] | null;
  /** Technologies assigned to this service via USES_TECH edges */
  technologies?: Technology[] | null;
  /** Count of technologies assigned via USES_TECH edges */
  technologyCount?: number | null;
  /**
   * Technology assignments (USES_TECH edges) with edge metadata — `notes`
   * and an optional pinned published `TechnologyVersion` (issue #1284).
   * Additive alongside `technologies` above, which stays a flat list.
   */
  technologyAssignments?: ServiceTechnologyAssignment[] | null;
  /** Diagrams documenting this service, via DOCUMENTED_BY edges (issue #1072/#1074/#1077). */
  diagrams?: Diagram[] | null;
  /**
   * Assessment cadence override for the recurring scheduler.
   * Absent = platform default (semi-annual).
   */
  assessmentCadence?: AssessmentCadence | null;
  _schemaVersion: string;
  _createdAt: string;
  _createdBy: string;
  _updatedAt: string;
  _updatedBy: string;
  _deletedAt?: string | null;
  _extensionData?: Record<string, unknown> | null;
}

/**
 * Service edge for pagination
 */
export interface ServiceEdge {
  node: Service;
  cursor: string;
}

/**
 * Service connection for paginated results
 */
export interface ServiceConnection {
  edges: ServiceEdge[];
  pageInfo: PageInfo;
  totalCount?: number | null;
}

/**
 * Input for filtering services
 */
export interface ServiceFilter {
  serviceType?: ServiceType;
  teamId?: string;
  lifecycleStatus?: ServiceLifecycleStatus;
  tier?: ServiceTier;
  includeDeleted?: boolean;
  search?: string;
  tagIds?: string[];
  /** Restrict to Services that use this Technology, via USES_TECH edges (issue #1815). */
  technologyId?: string;
}

export interface ServiceStats {
  total: number;
  byStatus: Record<string, number>;
  byTier: Record<string, number>;
}
